/**
 * UI-слой Рублокса — компоненты дизайн-системы.
 *
 * Единственный источник правды по кнопкам, полям, карточкам и состояниям
 * списков. Правило: если компонента здесь нет — сначала добавляем сюда,
 * потом используем на странице. Обход «только в этом одном месте» — это
 * и есть тот самый 722-й хардкодный цвет.
 *
 * Живая витрина системы: /app/ui (UiShowcase).
 */
import React from "react";
import "./ui.css";

const cx = (...parts) => parts.filter(Boolean).join(" ");

/* ── Кнопка ──────────────────────────────────────────────────────────
   variant по СМЫСЛУ, а не по цвету:
     primary — главное действие экрана, ровно одно;
     solid   — сильное, но не главное (сохранить, опубликовать);
     ghost   — обычное действие, сколько угодно;
     subtle  — переключаемое состояние (лайк, избранное);
     danger  — необратимое, всегда с подтверждением;
     link    — навигация, замаскированная под кнопку.

   loading оставляет ширину неизменной и сам выставляет disabled:
   кнопка, которая после клика выглядит так же, читается как сломанная. */
export function Button({
  variant = "ghost",
  size = "md",
  icon,
  iconOnly = false,
  loading = false,
  block = false,
  className,
  children,
  disabled,
  type = "button",
  ...rest
}) {
  const cls = cx(
    "ui-btn",
    `ui-btn--${variant}`,
    size !== "md" && `ui-btn--${size}`,
    iconOnly && "ui-btn--icon",
    loading && "ui-btn--loading",
    block && "ui-btn--block",
    className
  );
  return (
    <button type={type} className={cls} disabled={disabled || loading} {...rest}>
      {icon}
      {!iconOnly && children}
    </button>
  );
}

/* Ссылка, выглядящая как кнопка. Отдельный компонент, а не проп `as`:
   ссылке не нужны disabled/loading, а кнопке — href. */
export function ButtonLink({
  variant = "ghost",
  size = "md",
  icon,
  block = false,
  className,
  children,
  ...rest
}) {
  return (
    <a
      className={cx(
        "ui-btn",
        `ui-btn--${variant}`,
        size !== "md" && `ui-btn--${size}`,
        block && "ui-btn--block",
        className
      )}
      {...rest}
    >
      {icon}
      {children}
    </a>
  );
}

/* ── Пилюля ─────────────────────────────────────────────────────────
   Заливка --X-bg + текст --X. Пара контрастна в обеих темах, потому
   что -bg переопределяется вместе с темой. */
export function Pill({ tone = "neutral", icon, className, children, ...rest }) {
  return (
    <span className={cx("ui-pill", `ui-pill--${tone}`, className)} {...rest}>
      {icon}
      {children}
    </span>
  );
}

/* ── Карточка ───────────────────────────────────────────────────── */
export function Card({
  hover = false,
  flat = false,
  pad0 = false,
  accent = false,
  className,
  children,
  ...rest
}) {
  return (
    <div
      className={cx(
        "ui-card",
        hover && "ui-card--hover",
        flat && "ui-card--flat",
        pad0 && "ui-card--pad0",
        accent && "ui-card--accent",
        className
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

/* ── Поле ввода ─────────────────────────────────────────────────────
   Ошибка ВЫТЕСНЯЕТ подсказку, они не показываются вместе: две строки
   под полем — два конкурирующих сообщения, второе не читают. */
export function Input({
  label,
  hint,
  error,
  leadIcon,
  trailing,
  id,
  className,
  ...rest
}) {
  const autoId = React.useId();
  const inputId = id || autoId;
  return (
    <div className="ui-field">
      {label && (
        <label className="ui-field__label" htmlFor={inputId}>
          {label}
        </label>
      )}
      <div className="ui-field__wrap">
        {leadIcon && <span className="ui-field__lead">{leadIcon}</span>}
        <input
          id={inputId}
          className={cx(
            "ui-input",
            leadIcon && "ui-input--lead",
            trailing && "ui-input--trail",
            error && "ui-input--error",
            className
          )}
          aria-invalid={error ? true : undefined}
          {...rest}
        />
        {trailing && <span className="ui-field__trail">{trailing}</span>}
      </div>
      {error ? (
        <span className="ui-field__error">{error}</span>
      ) : (
        hint && <span className="ui-field__hint">{hint}</span>
      )}
    </div>
  );
}

export function Textarea({ label, hint, error, id, className, ...rest }) {
  const autoId = React.useId();
  const inputId = id || autoId;
  return (
    <div className="ui-field">
      {label && (
        <label className="ui-field__label" htmlFor={inputId}>
          {label}
        </label>
      )}
      <textarea
        id={inputId}
        className={cx("ui-textarea", error && "ui-textarea--error", className)}
        aria-invalid={error ? true : undefined}
        {...rest}
      />
      {error ? (
        <span className="ui-field__error">{error}</span>
      ) : (
        hint && <span className="ui-field__hint">{hint}</span>
      )}
    </div>
  );
}

/* ── Состояния списка ───────────────────────────────────────────────
   У любого списка их четыре: грузится / пусто / данные / сломалось.
   Пустое состояние ВСЕГДА имеет выход — слот action обязателен,
   пустой экран без действия это тупик. */
export function EmptyState({ icon, title, desc, action, className }) {
  return (
    <div className={cx("ui-empty", className)}>
      {icon && <span className="ui-empty__icon">{icon}</span>}
      <div className="ui-empty__title">{title}</div>
      {desc && <div className="ui-empty__desc">{desc}</div>}
      {action && <div className="ui-empty__action">{action}</div>}
    </div>
  );
}

/* Скелетон — когда форма контента ИЗВЕСТНА. Держит раскладку:
   после загрузки ничего не прыгает. */
export function Skeleton({ w = "100%", h = 16, r, className, style }) {
  return (
    <span
      className={cx("ui-skeleton", className)}
      style={{ display: "block", width: w, height: h, borderRadius: r, ...style }}
      aria-hidden="true"
    />
  );
}

/* Спиннер — когда грузится страница целиком и форма неизвестна.
   Скелетон всей страницы был бы ложью о будущей раскладке. */
export function Spinner({ size, center = false, className }) {
  return (
    <span
      className={cx(
        "ui-spinner",
        size === "sm" && "ui-spinner--sm",
        center && "ui-spinner--center",
        className
      )}
      style={center ? { display: "block" } : undefined}
      role="status"
      aria-label="Загрузка"
    />
  );
}

/* ── Навигация ──────────────────────────────────────────────────────
   Tabs — для РАЗНОГО содержимого (вкладка = раздел).
   Segmented — для ОДНОГО содержимого в разных видах, 2–3 варианта. */
export function Tabs({ items, value, onChange, className }) {
  return (
    <div className={cx("ui-tabs", className)} role="tablist">
      {items.map((it) => (
        <button
          key={it.id}
          type="button"
          role="tab"
          aria-selected={value === it.id}
          className={cx("ui-tab", value === it.id && "is-active")}
          onClick={() => onChange(it.id)}
        >
          {it.icon}
          {it.label}
        </button>
      ))}
    </div>
  );
}

export function Segmented({ items, value, onChange, className }) {
  return (
    <div className={cx("ui-seg", className)} role="group">
      {items.map((it) => (
        <button
          key={it.id}
          type="button"
          aria-pressed={value === it.id}
          className={cx("ui-seg__btn", value === it.id && "is-active")}
          onClick={() => onChange(it.id)}
        >
          {it.icon}
          {it.label}
        </button>
      ))}
    </div>
  );
}

/* ── Стат ───────────────────────────────────────────────────────── */
export function Stat({ value, label, className }) {
  return (
    <div className={cx("ui-stat", className)}>
      <div className="ui-stat__value">{value}</div>
      <div className="ui-stat__label">{label}</div>
    </div>
  );
}

/* ── Раскладка ──────────────────────────────────────────────────── */
export function Page({ wide = false, className, children }) {
  return (
    <div className={cx("ui-page", wide && "ui-page--wide", className)}>
      {children}
    </div>
  );
}

export function Section({ title, sub, more, className, children }) {
  return (
    <section className={cx("ui-section", className)}>
      {(title || more) && (
        <div className="ui-section__head">
          {title && <h2 className="ui-section__title">{title}</h2>}
          {more && <div className="ui-section__more">{more}</div>}
        </div>
      )}
      {sub && <p className="ui-section__sub">{sub}</p>}
      {children}
    </section>
  );
}

/* ── Форматирование чисел ───────────────────────────────────────────
   Свыше четырёх знаков человек без разделителя пересчитывает разряды
   глазами. В карточке важен порядок величины, а не точное число. */
export function fmtNum(n) {
  if (n == null) return "—";
  return Number(n).toLocaleString("ru");
}

/**
 * Русское склонение по числу: plural(11, "алмаз", "алмаза", "алмазов").
 *
 * Правило непростое, и «на глаз» его пишут неверно постоянно. В кошельке
 * стояло `n < 5 ? "а" : "ов"` — оно давало «11 алмаза» (должно «алмазов»)
 * и «21 алмазов» (должно «21 алмаз»): числа 11–14 ведут себя как «много»
 * независимо от последней цифры, а 21, 31, 101 — как «один».
 */
export function plural(n, one, few, many) {
  const abs = Math.abs(Number(n) || 0);
  const m10 = abs % 10;
  const m100 = abs % 100;
  if (m100 >= 11 && m100 <= 14) return many;   // 11–14 всегда «много»
  if (m10 === 1) return one;
  if (m10 >= 2 && m10 <= 4) return few;
  return many;
}

export function fmtCompact(n) {
  if (n == null) return "—";
  const v = Number(n);
  if (v < 1000) return String(v);
  if (v < 1000000) {
    const k = v / 1000;
    return `${k < 10 ? k.toFixed(1).replace(".", ",").replace(",0", "") : Math.round(k)} тыс.`;
  }
  const m = v / 1000000;
  return `${m < 10 ? m.toFixed(1).replace(".", ",").replace(",0", "") : Math.round(m)} млн`;
}
